import { openDB } from 'idb';
import type { MediaItem, UploadAllocation, UploadFileRequest } from '../../shared/types';
import { allocateUploads, completeUpload } from './api';
import { captureDateFor, prepareImage } from './images';

const CHUNK_SIZE = 8 * 1024 * 1024;
const ALLOCATION_BATCH_SIZE = 100;
const DB_NAME = 'big-bear-uploads';
const STORE_NAME = 'pending';
let activeUploads = 0;
const uploadWaiters: Array<() => void> = [];

const acquireUploadSlot = () => {
  if (activeUploads < 2) {
    activeUploads += 1;

    return Promise.resolve();
  }

  return new Promise<void>((resolve) => uploadWaiters.push(resolve));
};

const releaseUploadSlot = () => {
  const next = uploadWaiters.shift();

  if (next) {
    next();
  } else {
    activeUploads -= 1;
  }
};

export type UploadStatus = 'preparing' | 'queued' | 'uploading' | 'paused' | 'error' | 'complete';

export interface UploadTask {
  clientId: string;
  file: File;
  localUrl: string;
  preview?: Blob;
  capturedAt?: string;
  width?: number;
  height?: number;
  allocation?: UploadAllocation;
  originalOffset: number;
  previewOffset: number;
  uploadedBytes: number;
  totalBytes: number;
  status: UploadStatus;
  error?: string;
}

interface StoredUpload {
  clientId: string;
  file: File;
  preview?: Blob;
  capturedAt: string;
  width?: number;
  height?: number;
  allocation: UploadAllocation;
  originalOffset: number;
  previewOffset: number;
  createdAt: number;
}

const database = () =>
  openDB(DB_NAME, 1, {
    upgrade(db) {
      db.createObjectStore(STORE_NAME, { keyPath: 'clientId' });
    },
  });

const storeTask = async (task: UploadTask) => {
  if (!task.allocation || !task.capturedAt) return;

  const record: StoredUpload = {
    clientId: task.clientId,
    file: task.file,
    preview: task.preview,
    capturedAt: task.capturedAt,
    width: task.width,
    height: task.height,
    allocation: task.allocation,
    originalOffset: task.originalOffset,
    previewOffset: task.previewOffset,
    createdAt: Date.now(),
  };
  const db = await database();
  await db.put(STORE_NAME, record);
};

const removeStoredTask = async (clientId: string) => {
  const db = await database();
  await db.delete(STORE_NAME, clientId);
};

export const restoreUploads = async (): Promise<UploadTask[]> => {
  const db = await database();
  const records = (await db.getAll(STORE_NAME)) as StoredUpload[];

  return records.map((record) => {
    const expired = Date.now() - record.createdAt > 6 * 24 * 60 * 60 * 1000;

    return {
      ...record,
      allocation: expired ? undefined : record.allocation,
      localUrl: URL.createObjectURL(record.preview ?? record.file),
      uploadedBytes: expired ? 0 : record.originalOffset + record.previewOffset,
      totalBytes: record.file.size + (record.preview?.size ?? 0),
      status: expired ? 'error' : 'paused',
      error: expired ? 'Upload session expired. Retry to create a new session.' : undefined,
      originalOffset: expired ? 0 : record.originalOffset,
      previewOffset: expired ? 0 : record.previewOffset,
    };
  });
};

export const makeTasks = (files: File[]): UploadTask[] =>
  files.map((file) => ({
    clientId: crypto.randomUUID(),
    file,
    localUrl: URL.createObjectURL(file),
    originalOffset: 0,
    previewOffset: 0,
    uploadedBytes: 0,
    totalBytes: file.size,
    status: 'preparing',
  }));

const update = (
  task: UploadTask,
  patch: Partial<UploadTask>,
  onChange: (task: UploadTask) => void,
) => {
  Object.assign(task, patch);
  onChange({ ...task });
};

export const prepareTasks = async (
  tasks: UploadTask[],
  onChange: (task: UploadTask) => void,
) => {
  for (const task of tasks) {
    try {
      if (task.file.type.startsWith('image/')) {
        const result = await prepareImage(task.file);
        URL.revokeObjectURL(task.localUrl);
        task.preview = result.preview;
        task.localUrl = URL.createObjectURL(result.preview);
        task.capturedAt = result.capturedAt;
        task.width = result.width;
        task.height = result.height;
        task.totalBytes += result.preview.size;
      } else {
        task.capturedAt = await captureDateFor(task.file);
      }

      update(task, { status: 'queued' }, onChange);
    } catch (error) {
      update(
        task,
        {
          status: 'error',
          error: error instanceof Error ? error.message : 'Could not prepare this file',
        },
        onChange,
      );
    }
  }

  const ready = tasks.filter((task) => task.status === 'queued');

  if (ready.length === 0) return;

  await allocatePreparedTasks(ready, onChange);
};

export const allocatePreparedTasks = async (
  ready: UploadTask[],
  onChange: (task: UploadTask) => void,
) => {
  for (let start = 0; start < ready.length; start += ALLOCATION_BATCH_SIZE) {
    const batch = ready.slice(start, start + ALLOCATION_BATCH_SIZE);
    const requests: UploadFileRequest[] = batch.map((task) => ({
      clientId: task.clientId,
      name: task.file.name,
      mimeType: task.file.type,
      size: task.file.size,
      capturedAt: task.capturedAt!,
      preview:
        task.preview && task.width && task.height
          ? {
              mimeType: 'image/jpeg',
              size: task.preview.size,
              width: task.width,
              height: task.height,
            }
          : undefined,
    }));
    const result = await allocateUploads(requests);

    for (const task of batch) {
      task.allocation = result.uploads.find((upload) => upload.clientId === task.clientId);

      if (!task.allocation) {
        update(task, { status: 'error', error: 'The upload session was not created' }, onChange);
        continue;
      }

      await storeTask(task);
      onChange({ ...task });
    }
  }
};

const parseOffset = (response: Response, fallback: number) => {
  const range = response.headers.get('Range');
  const match = range?.match(/bytes=0-(\d+)/);

  return match ? Number(match[1]) + 1 : fallback;
};

const uploadBlob = async (
  task: UploadTask,
  blob: Blob,
  fileId: string,
  sessionUrl: string,
  offsetKey: 'originalOffset' | 'previewOffset',
  signal: AbortSignal,
  onChange: (task: UploadTask) => void,
) => {
  const relayUrl = `/api/uploads/${encodeURIComponent(fileId)}`;
  const relayHeaders = {
    'X-Drive-Upload-Session': sessionUrl,
  };
  const statusResponse = await fetch(relayUrl, {
    method: 'PUT',
    headers: {
      ...relayHeaders,
      'Content-Range': `bytes */${blob.size}`,
    },
    signal,
  });

  if (statusResponse.status === 404 || statusResponse.status === 410) {
    throw new Error('Upload session expired. Retry to create a new session.');
  }

  if (statusResponse.status !== 308 && !statusResponse.ok) {
    throw new Error(`Could not resume the Drive upload (${statusResponse.status})`);
  }

  const remoteOffset =
    statusResponse.status === 308 ? parseOffset(statusResponse, 0) : blob.size;
  task.uploadedBytes += remoteOffset - task[offsetKey];
  task[offsetKey] = remoteOffset;
  onChange({ ...task });
  await storeTask(task);

  while (task[offsetKey] < blob.size) {
    const start = task[offsetKey];
    const end = Math.min(start + CHUNK_SIZE, blob.size);
    const chunk = blob.slice(start, end);
    const response = await fetch(relayUrl, {
      method: 'PUT',
      headers: {
        ...relayHeaders,
        'Content-Range': `bytes ${start}-${end - 1}/${blob.size}`,
      },
      body: chunk,
      signal,
    });

    if (response.status !== 308 && !response.ok) {
      if (response.status === 404 || response.status === 410) {
        throw new Error('Upload session expired. Retry to create a new session.');
      }

      throw new Error(`Google Drive rejected the upload (${response.status})`);
    }

    const nextOffset = response.status === 308 ? parseOffset(response, end) : blob.size;
    const delta = nextOffset - task[offsetKey];
    task[offsetKey] = nextOffset;
    update(task, { uploadedBytes: task.uploadedBytes + delta }, onChange);
    await storeTask(task);
  }
};

export const uploadTask = async (
  task: UploadTask,
  signal: AbortSignal,
  onChange: (task: UploadTask) => void,
): Promise<MediaItem> => {
  await acquireUploadSlot();

  try {
    if (!task.allocation) throw new Error('Upload session is missing');

    update(task, { status: 'uploading', error: undefined }, onChange);

    try {
      if (task.preview && task.allocation.preview) {
        await uploadBlob(
          task,
          task.preview,
          task.allocation.preview.id,
          task.allocation.preview.sessionUrl,
          'previewOffset',
          signal,
          onChange,
        );
      }

      await uploadBlob(
        task,
        task.file,
        task.allocation.id,
        task.allocation.sessionUrl,
        'originalOffset',
        signal,
        onChange,
      );
      const { item } = await completeUpload(task.allocation.id);
      await removeStoredTask(task.clientId);
      update(task, { status: 'complete', uploadedBytes: task.totalBytes }, onChange);

      return item;
    } catch (error) {
      if (signal.aborted) {
        update(task, { status: 'paused', error: undefined }, onChange);
      } else {
        update(
          task,
          { status: 'error', error: error instanceof Error ? error.message : 'Upload failed' },
          onChange,
        );
      }

      throw error;
    }
  } finally {
    releaseUploadSlot();
  }
};

export const discardTask = async (task: UploadTask) => {
  URL.revokeObjectURL(task.localUrl);
  await removeStoredTask(task.clientId);
};
